import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { periodLabel } from '../gameUtils';
import { statsGameApi } from '../statsGameApi';

// Local 1Hz ticker, end-of-period prompt, and clock / period-length controls.
export function useGameClock({ gameId, clock, setClock, quarter, setQuarter, fetchAll }) {
  const [timerExpiredFor, setTimerExpiredFor] = useState(null);
  const prevRemainingRef = useRef(null);

  // Smooth countdown between 6s server syncs; stops at 0:00.
  useEffect(() => {
    if (!clock.running) return undefined;
    if ((clock.remaining_sec ?? 0) <= 0) return undefined;
    const id = setInterval(() => {
      setClock(c => {
        if (!c.running) return c;
        if (c.remaining_sec <= 0) return { ...c, remaining_sec: 0 };
        return { ...c, remaining_sec: c.remaining_sec - 1 };
      });
    }, 1000);
    return () => clearInterval(id);
  }, [clock.running, clock.remaining_sec, setClock]);

  // Fire once when a running clock crosses to 0: auto-pause and prompt.
  useEffect(() => {
    const cur = clock.remaining_sec ?? 0;
    const prev = prevRemainingRef.current;
    prevRemainingRef.current = cur;
    if (!clock.running || timerExpiredFor !== null) return;
    if (cur === 0 && (prev === null || prev > 0)) {
      statsGameApi.clockAction(gameId, 'pause').catch(() => { /* fetchAll will retry */ });
      setTimerExpiredFor(quarter);
    }
  }, [clock.remaining_sec, clock.running, quarter, gameId, timerExpiredFor]);

  const advanceToNextPeriod = async () => {
    const nextQuarter = quarter + 1;
    const targetLen = clock.quarter_length_sec || 600;
    // Flip the client clock first so the expiry effect doesn't re-fire for the new period.
    setClock(c => ({ ...c, remaining_sec: targetLen, running: false, started_at: null }));
    setQuarter(nextQuarter);
    setTimerExpiredFor(null);
    prevRemainingRef.current = targetLen;
    try {
      await statsGameApi.setQuarter(gameId, nextQuarter);
      await statsGameApi.setClock(gameId, { remaining_sec: targetLen, running: false });
      toast.success(`Starting ${periodLabel(nextQuarter)}`);
      fetchAll();
    } catch {
      toast.error('Could not advance the period');
    }
  };

  const toggleClock = async () => {
    try {
      await statsGameApi.clockAction(gameId, clock.running ? 'pause' : 'start');
      fetchAll();
    } catch { /* ignore */ }
  };

  const updateClock = async (body) => {
    try {
      await statsGameApi.setClock(gameId, body);
      fetchAll();
      return true;
    } catch {
      return false;
    }
  };

  const saveClock = (totalSec, alsoSetLength) => updateClock({
    remaining_sec: totalSec,
    running: false,
    ...(alsoSetLength ? { quarter_length_sec: totalSec } : {}),
  });

  const setPeriodLength = (minutes) => {
    const totalSec = minutes * 60;
    return updateClock({ quarter_length_sec: totalSec, remaining_sec: totalSec, running: false });
  };

  return {
    timerExpiredFor,
    dismissTimer: () => setTimerExpiredFor(null),
    advanceToNextPeriod,
    toggleClock,
    saveClock,
    setPeriodLength,
  };
}
